import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CircleCheck, FileJson, FileWarning, Loader, RefreshCw, ScrollText, Trash2 } from 'lucide-react';

import { ApiError } from '@/shared/api/ApiError';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import type { ImportRunSummary } from '../model/types';
import {
  useAbandonImport,
  useCommitImport,
  useImportRun,
  useImportRuns,
  useRequestAnalysis,
  useReuploadImport,
  useStartImport,
} from '../queries/useImport';
import { ConflictGroups } from './ConflictGroups';
import { ImportReport } from './ImportReport';
import { ImportRollback } from './ImportRollback';
import { ImportDropzone } from './ImportDropzone';
import { ImportFormatDialog } from './ImportFormatDialog';
import { ImportOptions } from './ImportOptions';
import { ImportStats } from './ImportStats';
import styles from './Import.module.css';

/**
 * Ekran importu (K5) — <b>przygotowany wsad, który człowiek ogląda przed zapisem</b>.
 *
 * ⭐ Ekran ma jeden stan wiodący i podąża za nim: <b>nie ma przebiegu</b> → wskazanie pliku ·
 * <b>analiza trwa</b> → uczciwe „liczę" · <b>wsad odrzucony</b> → powód i droga wyjścia ·
 * <b>są konflikty</b> → obróbka · <b>czysto</b> → zatwierdzenie. Wszystkie naraz na jednym
 * ekranie dałyby cztery puste sekcje przez większość czasu.
 */
export function ImportScreen() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const runs = useImportRuns();
  const start = useStartImport();

  /*
   * ⭐ Wybrany przebieg siedzi w ADRESIE, nie w stanie komponentu (spec 4A: „każdy istotny
   * stan ma własny, udostępnialny URL"). Dzięki temu raport z konkretnego wsadu da się wkleić
   * do Jiry albo na czacie — a odświeżenie strony nie wyrzuca człowieka na początek.
   */
  const [params, setParams] = useSearchParams();
  const wybrany = Number(params.get('run') ?? '');
  const wycofywany = Number(params.get('rollback') ?? '');

  const otwarty = useMemo(
    () => runs.data?.find((run) => run.state === 'open') ?? null,
    [runs.data],
  );

  const podglad = useMemo(
    () => runs.data?.find((run) => run.id === wybrany) ?? null,
    [runs.data, wybrany],
  );

  if (runs.isPending) {
    return (
      <div className={styles['screen']} aria-busy="true">
        <Skeleton width="18rem" height="2rem" announce />
        <Skeleton height="9rem" />
      </div>
    );
  }

  if (runs.isError) {
    return (
      <div className={styles['screen']}>
        <Failure error={runs.error} onRetry={() => void runs.refetch()} />
      </div>
    );
  }

  /*
   * ⭐ Wycofanie wygrywa nawet z raportem: to jedyna nieodwracalna operacja w produkcie, więc
   * gdy ktoś ma ją w adresie, nie pokazujemy mu niczego innego. ⚠ Tylko dla przebiegu
   * ZAPISANEGO — wycofanie otwartego nie ma sensu, a wycofanego już się nie da (R24).
   */
  const doWycofania = runs.data?.find((run) => run.id === wycofywany && run.state === 'committed');

  if (doWycofania !== undefined) {
    return (
      <ImportRollback
        run={doWycofania}
        onBack={() => {
          params.delete('rollback');
          setParams(params, { replace: true });
        }}
      />
    );
  }

  // Raport wygrywa z resztą: skoro ktoś wskazał konkretny przebieg, chce zobaczyć właśnie jego.
  if (podglad !== null && podglad.state !== 'open') {
    return (
      <ImportReport
        run={podglad}
        onBack={() => {
          params.delete('run');
          setParams(params, { replace: true });
        }}
        onRollback={() => {
          params.set('rollback', String(podglad.id));
          setParams(params, { replace: false });
        }}
      />
    );
  }

  if (otwarty === null) {
    return (
      <div className={styles['screen']}>
        <Header />

        <ImportDropzone
          busy={start.isPending}
          label={t('import.upload.label')}
          hint={t('import.upload.hint')}
          onPick={(file) =>
            start.mutate(file, {
              onSuccess: (result) => {
                if (result.sourceIsNew) {
                  // ⭐⭐ Najcichsza mina kontraktu: literówka w jednym polu na górze pliku
                  // („repo" → „repos") sprawia, że KAŻDY klucz jest nowy — import nie zgłasza
                  // ani jednego konfliktu i tworzy duplikat całego grafu. Sam wzorzec symbolu
                  // tego nie łapie, bo literówka też jest poprawnym symbolem.
                  show({
                    tone: 'danger',
                    durationMs: 20_000,
                    message: t('import.upload.newSource', { source: result.source }),
                  });
                }
              },
              onError: (error) => show({ tone: 'danger', message: describeError(error) }),
            })
          }
        />

        <RecentRuns
          runs={runs.data}
          onOpen={(id) => setParams({ run: String(id) })}
        />
      </div>
    );
  }

  return <OpenRun runId={otwarty.id} />;
}

/**
 * Awaria odczytu.
 *
 * ⭐ <b>„Spróbuj ponownie" pojawia się tylko wtedy, gdy ponowienie ma sens.</b> Przy odmowie
 * uprawnień (403) ten przycisk jest obietnicą bez pokrycia: kliknięcie da dokładnie ten sam
 * wynik, a użytkownik zostaje z wrażeniem, że coś się zacięło. Zamiast tego mówimy, kto ma
 * do tego dostęp — i zostawiamy drogę powrotu.
 */
function Failure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const forbidden = error instanceof ApiError && error.isForbidden;

  return (
    <div className={styles['failure']} role="alert">
      <p>{describeError(error)}</p>
      {forbidden ? null : <Button onClick={onRetry}>{t('common.action.retry')}</Button>}
    </div>
  );
}

function Header() {
  const { t } = useTranslation();

  /*
   * ⭐ Okno „Format pliku" stoi w ADRESIE (`?help=format`), nie w stanie komponentu — jak wybrany
   * przebieg (`?run=`): opis formatu da się podlinkować komuś, kto ma przygotować wsad, a odświeżenie
   * nie zamyka okna (spec 4A). Zamknięcie zdejmuje parametr, nic więcej nie rusza.
   */
  const [params, setParams] = useSearchParams();
  const formatOpen = params.get('help') === 'format';

  function setFormatOpen(open: boolean) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (open) next.set('help', 'format');
        else next.delete('help');
        return next;
      },
      { replace: true },
    );
  }

  return (
    <header className={styles['head']}>
      <div className={styles['headRow']}>
        <h1 className={styles['title']}>{t('import.title')}</h1>
        <Button variant="ghost" onClick={() => setFormatOpen(true)}>
          <FileJson size={15} aria-hidden="true" />
          {t('import.format.button')}
        </Button>
      </div>
      <p className={styles['lead']}>{t('import.lead')}</p>

      <ImportFormatDialog open={formatOpen} onOpenChange={setFormatOpen} />
    </header>
  );
}

function OpenRun({ runId }: { runId: number }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const run = useImportRun(runId);
  const analyze = useRequestAnalysis(runId);
  const reupload = useReuploadImport(runId);
  const commit = useCommitImport(runId);
  const abandon = useAbandonImport(runId);

  const analysis = run.data;

  useEffect(() => {
    // Wsad przyjęty, ale jeszcze nieprzeliczony — zlecamy analizę sami, zamiast prosić
    // człowieka o kliknięcie, którego znaczenia nie da się wytłumaczyć.
    if (
      analysis &&
      analysis.run.analyzedAtUtc === null &&
      analysis.run.analysisStartedAtUtc === null &&
      !analyze.isPending
    ) {
      analyze.mutate();
    }
  }, [analysis, analyze]);

  if (run.isPending) {
    return (
      <div className={styles['screen']} aria-busy="true">
        <Skeleton width="18rem" height="2rem" announce />
        <Skeleton height="6rem" />
        <Skeleton height="12rem" />
      </div>
    );
  }

  if (run.isError || analysis === undefined) {
    return (
      <div className={styles['screen']}>
        <Failure error={run.error} onRetry={() => void run.refetch()} />
      </div>
    );
  }

  const { run: summary, counts } = analysis;
  const trwa = summary.analysisInProgress === true;
  const padla = summary.analysisFailed === true;
  const odrzucony = summary.rejectionReasonKey !== null;

  // ⚠ Przebieg poza stanem „otwarty" jest już zapisem tego, co się stało — opcje zostają
  //   widoczne (raport ma mówić, na jakich zasadach wsad wszedł), ale nie do ruszenia.
  const readOnly = summary.state !== 'open';
  // Kontyngent elementów (dec. 154): „po zatwierdzeniu" ponad limit blokuje przycisk Z POWODEM,
  // zanim serwer odmówi pod blokadą. Liczby z tego samego warunku, którym commit liczy nowe.
  const quotaExceeded =
    analysis.elementsQuota.wouldExceed ?? analysis.elementsQuota.afterCommit > analysis.elementsQuota.max;
  const format = new Intl.NumberFormat(i18n.language);

  return (
    <div className={styles['screen']}>
      <Header />

      <RunBar summary={summary} />

      {trwa ? <Analysing startedAt={summary.analysisStartedAtUtc} /> : null}

      {/* Pad analizy w tle (dec. 136). ⛔ Bez tej gałęzi przebieg po padzie wyglądał
          identycznie jak „liczy się" — wieczne „trwa" bez żadnej drogi wyjścia; jedyny
          ślad siedział w logu hosta. Ponowienie jest tanie i zwykle wystarcza. */}
      {padla ? (
        <div className={styles['rejected']} role="alert">
          <FileWarning size={18} aria-hidden="true" />
          <div>
            <p className={styles['rejectedTitle']}>{t('import.analysisFailed.title')}</p>
            <p className={styles['rejectedBody']}>{t('import.analysisFailed.body')}</p>
            <Button
              onClick={() =>
                analyze.mutate(undefined, {
                  onError: (error) => show({ tone: 'danger', message: describeError(error) }),
                })
              }
              disabled={analyze.isPending}
            >
              {t('import.analysisFailed.retry')}
            </Button>
          </div>
        </div>
      ) : null}

      {odrzucony ? (
        <div className={styles['rejected']} role="alert">
          <FileWarning size={18} aria-hidden="true" />
          <div>
            <p className={styles['rejectedTitle']}>{t('import.rejected.title')}</p>
            <p className={styles['rejectedBody']}>
              {/* ⚠ `Error.Code` JEST kluczem i18n (spec 4C) — nie doklejamy do niego prefiksu. */}
              {t(summary.rejectionReasonKey ?? '', {
                defaultValue: t('import.rejected.generic'),
              })}
            </p>
            {/* ⭐ Odrzucenie jest tanie właśnie dzięki tej drodze: typowy wsad to JEDNA pomyłka
                generatora, więc główną odpowiedzią jest poprawienie pliku, a nie klikanie. */}
            <p className={styles['rejectedHint']}>{t('import.rejected.hint')}</p>
          </div>
        </div>
      ) : null}

      {/*
        ⭐ Sekcja opcji stoi NAD liczbami i nad listą konfliktów, bo to ona je zmienia:
        przełącznik „przypisuj także kontom nieaktywnym" zdejmuje albo dokłada cały rodzaj
        konfliktu, a „nadpisuj nazwy" zmienia liczbę, na której stoi decyzja o zatwierdzeniu.
        ⚠ Przy przebiegu zamkniętym opcje są ZAPISEM tego, co się stało — widoczne, nieedytowalne.
      */}
      {!odrzucony ? (
        <ImportOptions runId={runId} options={summary.options} readOnly={readOnly} />
      ) : null}

      {!trwa && !odrzucony && !padla ? <ImportStats analysis={analysis} /> : null}

      <div className={styles['toolbar']}>
        <ImportDropzone
          compact
          busy={reupload.isPending}
          label={t('import.upload.again')}
          onPick={(file) =>
            reupload.mutate(file, {
              onSuccess: () => {
                analyze.mutate();
                show({ tone: 'success', message: t('import.upload.replaced') });
              },
              onError: (error) => show({ tone: 'danger', message: describeError(error) }),
            })
          }
        />

        <Button
          variant="ghost"
          disabled={analyze.isPending || trwa}
          onClick={() => analyze.mutate()}
        >
          <RefreshCw size={15} aria-hidden="true" />
          {t('import.action.refresh')}
        </Button>

        <Button
          variant="ghost"
          disabled={abandon.isPending}
          onClick={() =>
            abandon.mutate(undefined, {
              onSuccess: () => show({ message: t('import.action.abandoned') }),
              onError: (error) => show({ tone: 'danger', message: describeError(error) }),
            })
          }
        >
          <Trash2 size={15} aria-hidden="true" />
          {t('import.action.abandon')}
        </Button>
      </div>

      {!trwa && !odrzucony ? (
        <ConflictGroups runId={runId} groups={analysis.groups} readOnly={false} />
      ) : null}

      {!trwa && !odrzucony ? (
        <div className={styles['commitBar']}>
          {/* ⭐ Wyłączony przycisk NIESIE POWÓD. Napis „169 pozycji czeka na decyzję" mówi,
              co zrobić; wyszarzone „Zatwierdź import" nie mówi nic — a to jest różnica
              między ekranem, który prowadzi, a ekranem, który blokuje. */}
          <output className={styles['commitReason']}>
            {counts.conflictsWaiting > 0
              ? t('import.commit.blocked', { count: counts.conflictsWaiting })
              : quotaExceeded
                ? t('import.commit.quotaBlocked', {
                    count: analysis.elementsQuota.afterCommit,
                    max: format.format(analysis.elementsQuota.max),
                  })
                : t('import.commit.ready', {
                    count: counts.newElements + counts.updatedElements,
                  })}
          </output>

          <Button
            variant="primary"
            disabled={counts.conflictsWaiting > 0 || quotaExceeded || commit.isPending}
            onClick={() =>
              commit.mutate(undefined, {
                onSuccess: () =>
                  show({ tone: 'success', message: t('import.commit.done') }),
                onError: (error) => show({ tone: 'danger', message: describeError(error) }),
              })
            }
          >
            <CircleCheck size={16} aria-hidden="true" />
            {t('import.commit.action')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RunBar({ summary }: { summary: ImportRunSummary }) {
  const { t, i18n } = useTranslation();

  const when = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(summary.startedAtUtc));

  return (
    <div className={styles['runBar']}>
      <span className={styles['runFile']}>{summary.fileName ?? t('import.run.noName')}</span>
      <span className={styles['runMeta']}>
        {t('import.run.meta', { source: summary.source, version: summary.fileVersion, when })}
      </span>
    </div>
  );
}

/**
 * Stan „analiza trwa".
 *
 * ⛔⛔ <b>Świadomie BEZ licznika „przeanalizowano 34 000 ze 100 000"</b>, mimo że taki napis
 * stoi w makiecie. Faza 1 to <b>kilka instrukcji zbiorowych SQL</b> — inaczej sto tysięcy
 * wierszy liczyłoby się godzinami — a instrukcja zbiorowa <b>nie ma postępu wierszowego</b>,
 * który dałoby się odczytać. Licznik byłby zmyślony, a zmyślony postęp jest gorszy niż jego
 * brak: uczy, że paskom na tym ekranie nie należy wierzyć.
 */
function Analysing({ startedAt }: { startedAt: string | null }) {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (startedAt === null) return undefined;

    const started = new Date(startedAt).getTime();
    const tick = () => setSeconds(Math.max(0, Math.round((Date.now() - started) / 1000)));

    tick();
    const timer = globalThis.setInterval(tick, 1000);

    return () => globalThis.clearInterval(timer);
  }, [startedAt]);

  return (
    <output className={styles['analysing']} aria-live="polite">
      <Loader className={styles['spinner']} size={16} aria-hidden="true" />
      <span>{t('import.analysing.title')}</span>
      <span className={styles['analysingElapsed']}>
        {t('import.analysing.elapsed', { seconds })}
      </span>
    </output>
  );
}

function RecentRuns({
  runs,
  onOpen,
}: {
  runs: readonly ImportRunSummary[];
  onOpen: (id: number) => void;
}) {
  const { t, i18n } = useTranslation();

  if (runs.length === 0) {
    return (
      <p className={styles['emptyRuns']}>{t('import.runs.empty')}</p>
    );
  }

  const format = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <section className={styles['runs']}>
      <h2 className={styles['runsTitle']}>{t('import.runs.title')}</h2>

      <ul className={styles['runsList']}>
        {runs.slice(0, 10).map((run) => (
          <li key={run.id} className={styles['runItem']}>
            <button type="button" className={styles['runLink']} onClick={() => onOpen(run.id)}>
              <ScrollText size={15} aria-hidden="true" />
              <span className={styles['runItemName']}>
                {run.fileName ?? t('import.run.noName')}
              </span>
            </button>
            <span className={styles['runItemState']} data-state={run.state}>
              {t(`import.runState.${run.state}`)}
            </span>
            <span className={styles['runItemWhen']}>{format.format(new Date(run.startedAtUtc))}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
